/**
 * Настройки доски. Макет data/Задачник.dc.html, boardSettingsOpen.
 *
 * §9.1: ЕДИНСТВЕННАЯ точка входа — шестерёнка. Разрозненных настроек
 * по другим экранам быть не должно.
 * §9.5: порядок столбцов меняется ТОЛЬКО здесь; на самой доске столбцы
 * не перетаскиваются.
 *
 * Вкладки разграничены правами: без board.members вкладка участников
 * не показывается — но и сервер откажет независимо от интерфейса (§1.4).
 */

import { Permission } from "@taskflow/contracts";
import { Tags } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";

import type { WsError } from "@taskflow/client-core";
import { Button, cn, ErrorState, SkeletonRows } from "@/shared/ui";
import { Columns3, Settings, Users } from "@/shared/ui/motionIcons";

import { useBoardQuery } from "@taskflow/client-core/boards";
import { ColumnsSection } from "./ColumnsSection";
import { GeneralSection } from "./GeneralSection";
import { MembersSection } from "./MembersSection";
import { TagsSection } from "./TagsSection";

type TabKey = "general" | "members" | "columns" | "tags";

interface TabDef {
  key: TabKey;
  label: string;
  icon: LucideIcon;
  permission: Permission;
}

const TABS: TabDef[] = [
  { key: "general", label: "Общие", icon: Settings, permission: Permission.BOARD_SETTINGS },
  { key: "members", label: "Участники", icon: Users, permission: Permission.BOARD_MEMBERS },
  { key: "columns", label: "Столбцы", icon: Columns3, permission: Permission.BOARD_COLUMNS },
  { key: "tags", label: "Теги", icon: Tags, permission: Permission.TAG_MANAGE },
];

export function BoardSettingsPage() {
  const { boardId = "" } = useParams<{ boardId: string }>();
  const board = useBoardQuery(boardId, { skip: boardId === "" });
  const permissions = board.data?.permissions ?? [];

  // В личную доску пользователей не добавить — раздела участников у неё нет.
  const allowed = TABS.filter(
    (tab) =>
      permissions.includes(tab.permission) &&
      !(tab.key === "members" && board.data?.isPersonal === true),
  );
  // Раздел можно открыть ссылкой: «Участники доски» в шапке ведут на ?tab=members.
  const [searchParams] = useSearchParams();
  const requested = TABS.find((tab) => tab.key === searchParams.get("tab"))?.key;
  const [active, setActive] = useState<TabKey>(requested ?? "general");

  if (board.isLoading) return <SkeletonRows rows={5} className="p-6" />;

  if (board.error !== undefined) {
    return (
      <ErrorState
        title="Настройки недоступны"
        message={(board.error as WsError).data.message}
        onRetry={() => void board.refetch()}
      />
    );
  }

  if (!board.data) return null;

  if (allowed.length === 0) {
    return (
      <ErrorState
        title="Нет доступа к настройкам"
        message="Для изменения настроек доски нужны отдельные права. Обратитесь к руководителю доски."
      />
    );
  }

  const current = allowed.some((tab) => tab.key === active) ? active : allowed[0]?.key;

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-4 p-5">
      <header className="flex items-center justify-between gap-4">
        <div className="min-w-0">
          <h1 className="truncate text-lg font-extrabold text-fg">Настройки доски</h1>
          <p className="truncate text-[13px] text-muted">{board.data.name}</p>
        </div>
        <Link to={`/boards/${boardId}`}>
          <Button variant="secondary" size="sm">
            К доске
          </Button>
        </Link>
      </header>

      <nav className="flex gap-1 border-b border-line" aria-label="Разделы настроек">
        {allowed.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActive(tab.key)}
            aria-current={current === tab.key ? "page" : undefined}
            className={cn(
              "flex items-center gap-1.5 border-b-2 px-3 py-2 text-[13px] font-semibold transition-colors",
              current === tab.key
                ? "border-accent text-fg"
                : "border-transparent text-muted hover:text-fg",
            )}
          >
            <tab.icon className="size-4" aria-hidden />
            {tab.label}
          </button>
        ))}
      </nav>

      {current === "general" && <GeneralSection board={board.data} />}
      {current === "members" && <MembersSection boardId={boardId} permissions={permissions} />}
      {current === "columns" && <ColumnsSection board={board.data} permissions={permissions} />}
      {current === "tags" && <TagsSection boardId={boardId} />}
    </div>
  );
}
